import { z } from 'zod'
import { AddressSchema } from '../../domains/address'
import { email, optionalString, requiredString } from '../../primitives'
import { MESSAGES } from '../../utils/messages'
import { createProgressiveSchema } from '../../utils/progressive'
import { BoardMemberSchema } from './board-members'
import { CommercialSocietySchema, OtherSocietySchema } from './constitution'
import { ShareholdingStructureSchema } from './shareholders'

/**
 * Identificación de la persona jurídica (empresa) — sección de datos del flujo del
 * portal para clientes `juridical`. Estructurado (NO Mixed), espejo enfocado del FEIC
 * interno `features/juridical/models/juridical-schema.ts`. Cubre identificación +
 * actividad económica + domicilio + contacto + estructura accionaria + órganos
 * superiores + constitución/registro (adaptativa por tipo de entidad).
 */

const base = z.object({
  // Tipo de entidad (lucrativa/no lucrativa, etc.). El detalle/otro son condicionales
  // del front; acá se capturan como strings (catálogo en la capa web).
  entityType: requiredString(),
  entityDetail: optionalString(),
  entityOther: optionalString(),
  companyName: requiredString(), // razón social
  tradeName: optionalString(), // nombre comercial
  registrationDate: requiredString(), // fecha de inscripción
  jurisdiction: requiredString(), // jurisdicción / país de constitución
  // ¿La entidad es una sociedad en formación? (sólo aplica a lucrativas). Relaja la
  // exigencia de NIT/actividad económica igual que una jurisdicción extranjera.
  societyFormation: z.boolean(),
  // NIT / actividad económica: requeridos para el caso local y no-en-formación. La
  // regla condicional (opcionales si es extranjera o sociedad en formación) la aplica
  // el `resolveSchema` de la capa web con un superRefine sobre `jurisdiction`.
  nit: optionalString(),
  economicActivity: optionalString(), // actividad económica específica
  economicActivityRTU: optionalString(),
  cpe: z.boolean(), // ¿contratista/proveedor del Estado?
  address: AddressSchema, // domicilio fiscal de la entidad
  // Contacto: teléfono(s) y correo(s). El FEIC jurídico admite VARIOS correos de
  // correspondencia (a diferencia del individual, que es uno solo) — se captura como
  // arreglo con al menos uno válido.
  phone: z.array(requiredString()).min(1, MESSAGES.REQUIRED),
  email: z.array(email()).min(1, MESSAGES.REQUIRED),
  website: optionalString(),
  // Constitución/registro — adaptativa por tipo de entidad. `commercialSociety` para
  // lucrativas (escritura + patentes); `otherSociety` para no lucrativas / organismos
  // (documento de creación + registro). Sólo se llena la que aplica al tipo.
  commercialSociety: CommercialSocietySchema.optional(),
  otherSociety: OtherSocietySchema.optional(),
  // Estructura accionaria (sólo entidades lucrativas). `shareholding` es el toggle
  // ("¿posee accionistas al 10%+?"); si es `true` se capturan los accionistas. Opcionales
  // a nivel schema — su exigencia depende del tipo de entidad (regla de la capa web).
  shareholding: z.boolean().optional(),
  shareholdingStructure: ShareholdingStructureSchema.optional(),
  // Integración del órgano superior (junta directiva) — sólo lucrativas con accionistas.
  membersOfBoard: z.array(BoardMemberSchema).optional()
})

/**
 * La forma de la empresa SIN la progresión por niveles.
 *
 * `CompanyApplicant.forLevel()` es un schema de ENTRADA: modela qué campos son
 * obligatorios en cada paso. Un contrato de LECTURA no tiene pasos —devuelve lo
 * que hay guardado— así que consume esta base directamente.
 */
export const CompanyApplicantShape = base

/**
 * Validación progresiva (igual patrón que `IndividualApplicant`). El backend deriva el
 * nivel del estado del expediente. `forLevel` deja requeridos sólo los campos del nivel.
 */
export const CompanyApplicant = createProgressiveSchema({
  base,
  levels: {
    // Mínimo para identificar la empresa.
    draft: ['companyName', 'nit'],
    // Sección "Datos de la empresa" del MVP.
    partial: [
      'entityType',
      'companyName',
      'registrationDate',
      'jurisdiction',
      'societyFormation',
      'nit',
      'economicActivity',
      'cpe',
      'address',
      'phone',
      'email'
    ],
    // Expediente completo (suma constitución/registro y datos secundarios).
    complete: [
      'entityType',
      'entityDetail',
      'companyName',
      'tradeName',
      'registrationDate',
      'jurisdiction',
      'societyFormation',
      'nit',
      'economicActivity',
      'economicActivityRTU',
      'cpe',
      'address',
      'phone',
      'email',
      'website',
      'commercialSociety',
      'otherSociety'
    ]
  }
})

export type CompanyApplicant = z.infer<typeof base>
